import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, Tags, UserRoundPlus } from 'lucide-react';
import { useHasPermission } from '@shared/lib/permissions';
import { useExperienceStore } from '@shared/stores/experienceStore';
import { useModuleStore } from '@shared/stores/moduleStore';
import { useQuickLinksStore, homeQuickLinks } from '@shared/stores/quickLinksStore';
import { runTileAction, isPanicTile } from '@shared/lib/tileActions';
import { useVisibleTiles, useVisibleCategories } from '@features/dashboard/useVisibleTiles';
import type { QuickLinkItem } from '@features/dashboard/QuickLinkTile';
import type { CompiledMoreTileCategory, CompiledPublicTile } from '@shared/types/domainConfig';

/**
 * Every quick link the member can put on Home, flattened from its four
 * sources into one list: the permission-gated admin shortcuts, tenant tiles
 * and `more_categories` groups (which open `onOpenCategory`). Also returns the
 * links Home shows, and the visible tiles and groups for the classic grid.
 */
export function useQuickLinks(onOpenCategory?: (category: CompiledMoreTileCategory) => void): {
  links: QuickLinkItem[];
  homeLinks: QuickLinkItem[];
  tiles: CompiledPublicTile[];
  categoryTiles: CompiledMoreTileCategory[];
} {
  const navigate = useNavigate();
  const canMemberRequests = useHasPermission('members:requests:approve');
  // Two independent hooks, combined after both run unconditionally — short-circuiting
  // `&&` across hook calls would make the second call conditional (Rules of Hooks).
  const hasBroadcastPermission = useHasPermission('communication:bulk_communications:publish');
  const communicationModuleEnabled = useModuleStore((s) => s.isModuleEnabled('communication'));
  const canBroadcast = hasBroadcastPermission && communicationModuleEnabled;
  const experience = useExperienceStore((s) => s.experience);
  const moreCategories = useExperienceStore((s) => s.getMoreCategories());

  // The backend compiles `tiles.main` per tenant by module, so render it as-is
  // apart from what only the client can know: whether this member's role can
  // open the module, and whether an eForm tile's form is still accepting
  // submissions (see `useVisibleTiles`).
  const allTiles = useMemo(
    () => [...(experience?.tiles?.main ?? [])].sort((a, b) => a.order - b.order),
    [experience],
  );
  const tiles = useVisibleTiles(allTiles);

  // Groups left with no reachable tile drop out with their tiles.
  const categoryTiles = useVisibleCategories(moreCategories);

  const links = useMemo<QuickLinkItem[]>(() => {
    const links: QuickLinkItem[] = [];

    // Admin: pending member join requests (members:requests:approve).
    if (canMemberRequests) {
      links.push({
        id: 'member-requests',
        label: 'Member requests',
        fallbackIcon: UserRoundPlus,
        isAdmin: true,
        onSelect: () => navigate('/app/member-requests'),
      });
    }

    // Admin: broadcast an SMS/in-app message (communication:bulk_communications:publish).
    if (canBroadcast) {
      links.push({
        id: 'send-message',
        label: 'Send message',
        fallbackIcon: Megaphone,
        isAdmin: true,
        onSelect: () => navigate('/app/admin/communication'),
      });
    }

    for (const tile of tiles) {
      links.push({
        id: tile.id,
        label: tile.label,
        imageUrl: tile.image_url,
        isPanic: isPanicTile(tile),
        onSelect: () => runTileAction(tile, navigate),
      });
    }

    for (const category of categoryTiles) {
      links.push({
        id: `category:${category.category_id}`,
        label: category.name,
        fallbackIcon: Tags,
        onSelect: () => onOpenCategory?.(category),
      });
    }

    return links;
  }, [tiles, categoryTiles, canMemberRequests, canBroadcast, navigate, onOpenCategory]);

  // The member's picks (up to 8), or until they pick, the first 8.
  const pinned = useQuickLinksStore((s) => s.pinned);
  const homeLinks = useMemo(() => homeQuickLinks(links, pinned), [links, pinned]);

  return { links, homeLinks, tiles, categoryTiles };
}
